import { UpdateBody, UpdateResponse } from '@cbo/shared-library/response';
import { UseMutationResult, useMutation } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import useInventoryRequests from '.';
import { VendorItemData, VendorItemDto, UpdateVendorItemDto } from '../models/VendorItem';
import { Paginated } from '../../models/Paginated';
import { RawItemData, RawItemDto, InventoryUnitMappingDto, UpdateUsagePerDto } from '../models/RawItem';
import { HttpsError } from '../../contexts/firebaseApiContext';
import { CustomUnit, CustomUnitDto, CustomUnitMappingDto } from '../models/UnitsOfMeasure';
import { CreateInvoiceItemDto, InvoiceItemData, UpdateInvoiceItemDto } from '../models/InvoiceItem';
import { UpsertOrderDeliveryPairs, VendorData, VendorDto } from '../models/Vendor';
import { InvoiceData, InvoiceDto, InvoiceStatusAction, OcrMetaDataDto, UpdateOcrMetaDataDto } from '../models/Invoice';
import {
  CreateInventorySummaryDto,
  InventorySummaryList,
  LocationDto,
  UpdateCountSummary,
  UpdateListItemUnitDto,
  UpdateLocationItemUnitDto,
  UpdateLocationSummary,
} from '../models/Location';
import {
  CreateRecipeDto,
  CreateRecipeResponse,
  RecipeCloneDto,
  UpdateRecipeDto,
  UpdateRecipeResponse,
} from '../models/Recipe';
import { IApiError } from '../../services/types';
import { EntityMappingData, MappingId, SalesItemMappingData } from '../models/SalesItem';
import { RecipeItemData, UpdateRecipeItemDto } from '../models/RecipeItem';
import { RecipeItemUnitPriceRequest, RecipeItemUnitPriceResponse } from '../types';
import {
  CreatePurchaseOrderDto,
  CreatePurchaseOrderItemDto,
  CreateSuggestedPurchaseOrderDto,
  CreateSuggestedPurchaseOrderResponseDto,
  GeneratePoNumberResponseDto,
  PurchaseOrderDetails,
  PurchaseOrderItem,
  UpdatePurchaseOrderItemDto,
} from '../models/PurchaseOrders';
import { FoodCostAction } from '../RecipeDetailsPage/RecipeInformationForm/FoodCostChangeDialog';
import { CreateInvoiceTemplateDto, InvoiceTemplate } from '../models/InvoiceTemplate';

interface UpdateCustomUnitVariables {
  customUnitId: string;
  customUnitDto: CustomUnitDto;
}
interface UpdateRawItemStatusVariables {
  rawItemId: string;
  active: boolean;
  futureDeactivationDate: string;
}

interface BulkUpdateVendorItemVariables {
  items: VendorItemData[];
  rawItemId: string;
  mappedVendorItemData: Paginated<VendorItemData> | undefined;
}

interface BulkCreateVendorItemVariables {
  items: VendorItemData[];
  rawItemId: string;
  vendorItemData: Paginated<VendorItemData> | undefined;
}

interface NoDeactivationItemVariables {
  name: string | undefined;
  categoryId: string | undefined;
  isActive: boolean | undefined;
}

interface UpdateReportingRecipeUnits {
  rawItemId: string;
  recipeUnit: string;
  reportingUnit: string;
}

interface UpdateRawItemVariables {
  rawItemId: string;
  rawItemDto: RawItemDto;
}

interface MapCustomUnitVariables {
  customUnitMappingDto: CustomUnitMappingDto;
  rawItemId: string;
}
interface MapIndustryUnitVariables {
  unitId: InventoryUnitMappingDto;
  rawItemId: string;
}
interface DeleteInventoryUnitVariables {
  inventoryUnitId: string;
  rawItemId: string;
}

interface DeleteRawItemVariables {
  rawItemId: string;
}

interface CreateInvoiceItemVariables {
  invoiceId: string;
  invoiceItemDto: CreateInvoiceItemDto;
}
interface UpdateInvoiceItemVariables {
  invoiceId: string;
  invoiceItemId: string;
  invoiceItemDto: UpdateInvoiceItemDto;
}

interface DeleteInvoiceItemVariables {
  invoiceId: string;
  invoiceItemId: string;
}
interface CreateVendorItemVariables {
  vendorId: string;
  vendorItemDto: VendorItemDto;
}
interface UpdateVendorItemVariables {
  vendorId: string;
  vendorItemId: string;
  vendorItemDto: UpdateVendorItemDto;
}

interface UpdateVendorItemStatusVariables {
  vendorId: string;
  vendorItemId: string;
  isActive: boolean;
}

interface UpdateVendorVariables {
  vendorId: string;
  vendorDto: UpdateVendorItemDto;
}
interface SetVendorActiveVariables {
  vendorId: string;
  isActive: boolean;
}

interface UpdateInvoiceStatusVariables {
  id: string;
  status: InvoiceStatusAction;
  newItems?: boolean;
}

interface DeleteInvoiceVariables {
  invoiceId: string;
  isFinalized?: boolean;
}

interface UpdateInvoiceVariables {
  invoiceId: string;
  invoiceDto: Partial<InvoiceDto>;
}
interface UpdateOcrMetadataVariables {
  invoiceId: string;
  enterpriseUnit: string;
  ocrMetadata: UpdateOcrMetaDataDto;
}

interface AddOcrMetadataVariables {
  invoiceId: string;
  enterpriseUnit: string;
  ocrMetadata: OcrMetaDataDto;
}

interface UpdateLocationItemVariables {
  locationId: string;
  body: UpdateLocationItemUnitDto[];
}

interface UpdateInventoryListVariables {
  listId: string;
  body: UpdateListItemUnitDto[];
}

interface DeleteLocationItemVariables {
  locationId: string;
  locationItemId: string;
}

interface CloneRecipeSchema {
  recipeId: string;
  body: RecipeCloneDto;
}

interface UpdateRecipeVariables {
  recipeId: string;
  body: UpdateRecipeDto;
  foodCostAction?: FoodCostAction;
}

interface DeleteRecipeVariables {
  recipeId: string;
}

interface UpdateLocationSummaryVariables {
  summaryId: string;
  locationId: string;
  body: UpdateLocationSummary;
  includePrepItems?: boolean;
}

interface UpdateSummaryVariables {
  summaryId: string;
  body: UpdateCountSummary;
  includePrepItems?: boolean;
}

interface UpdateSalesItemMappingVariables {
  id: string;
  body: EntityMappingData;
}
interface UpdateRecipeItemSchema {
  recipeItemId: string;
  body: UpdateRecipeItemDto;
  foodCostAction?: FoodCostAction;
}

interface BulkUpdateRecipeItemsSchema {
  recipeId: string;
  body: RecipeItemData[];
}

interface BulkUpdateLocationsSchema {
  body: {
    locations: LocationDto[];
  };
}

interface UpsertOrderDeliveryPairsVariables {
  id: string;
  body: UpsertOrderDeliveryPairs[];
}

interface CreateInvoiceTemplateVariables {
  vendorId: string;
  body: CreateInvoiceTemplateDto;
}

export const useCreateRecipe = () => {
  const { createRecipe } = useInventoryRequests();
  return useMutation<CreateRecipeResponse, IApiError, CreateRecipeDto, unknown>(
    (createRecipePayload: CreateRecipeDto) => createRecipe(createRecipePayload)
  );
};

export const useCloneRecipe = () => {
  const { cloneRecipe } = useInventoryRequests();
  async function mutationFunc({ recipeId, body }: CloneRecipeSchema) {
    const results = await cloneRecipe(recipeId, body);
    return results;
  }
  return useMutation<CreateRecipeResponse, IApiError, CloneRecipeSchema, unknown>(mutationFunc, {});
};

export const useUpdateRecipe = () => {
  const { updateRecipe } = useInventoryRequests();
  async function mutationFunc({ recipeId, body, foodCostAction }: UpdateRecipeVariables) {
    const results = await updateRecipe(recipeId, body, foodCostAction);
    return results;
  }
  return useMutation<UpdateRecipeResponse, IApiError, UpdateRecipeVariables, unknown>(mutationFunc, {});
};

export const useDeleteRecipe = () => {
  const { deleteRecipe } = useInventoryRequests();
  async function mutationFunc({ recipeId }: DeleteRecipeVariables) {
    const results = await deleteRecipe(recipeId);
    return results;
  }
  return useMutation<unknown, IApiError, DeleteRecipeVariables, unknown>(mutationFunc, {});
};

export const useDeleteCustomUnitMutation = (): UseMutationResult<
  string | void,
  unknown,
  {
    customUnitId: string;
  },
  unknown
> => {
  const { deleteCustomUnitById } = useInventoryRequests();
  return useMutation(({ customUnitId }) => deleteCustomUnitById(customUnitId), {
    useErrorBoundary: true,
  });
};

export const useCreateRecipeItem = () => {
  const { createRecipeItem } = useInventoryRequests();
  return useMutation(
    ({
      createRecipeItemPayload,
      foodCostAction,
    }: {
      createRecipeItemPayload: RecipeItemData;
      foodCostAction?: FoodCostAction;
    }) => createRecipeItem(createRecipeItemPayload, foodCostAction)
  );
};

export const useUpdateRecipeItem = () => {
  const { updateRecipeItem } = useInventoryRequests();
  async function mutationFunc({ recipeItemId, body, foodCostAction }: UpdateRecipeItemSchema) {
    const results = await updateRecipeItem(recipeItemId, body, foodCostAction);
    return results;
  }
  return useMutation<UpdateResponse, unknown, UpdateRecipeItemSchema, unknown>(mutationFunc, {});
};

/* istanbul ignore next */
export const useBulkUpdateRecipeItems = () => {
  const { bulkUpdateRecipeItems } = useInventoryRequests();
  async function mutationFunc({ recipeId, body }: BulkUpdateRecipeItemsSchema) {
    const results = await bulkUpdateRecipeItems(recipeId, body);
    return results;
  }
  return useMutation<UpdateResponse, unknown, BulkUpdateRecipeItemsSchema, unknown>(mutationFunc, {});
};

export const useDeleteRecipeItem = () => {
  const { deleteRecipeItem } = useInventoryRequests();
  return useMutation(
    ({
      recipeItemId,
      foodCostAction,
      recalculatedCostEffectiveDate,
    }: {
      recipeItemId: string;
      foodCostAction?: FoodCostAction;
      recalculatedCostEffectiveDate?: Date;
    }) => deleteRecipeItem(recipeItemId, foodCostAction, recalculatedCostEffectiveDate)
  );
};

export const useBulkUpdateVendorItemMappingMutation = () => {
  const { bulkUpdateVendorItemMapping } = useInventoryRequests();

  async function mutationFunc({ items, rawItemId, mappedVendorItemData }: BulkUpdateVendorItemVariables) {
    const itemsToMap = items
      .filter((item) => mappedVendorItemData?.items.filter((a) => a.id === item.id))
      .map((item) => ({ rawItemId, vendorId: item.vendorId, vendorItemId: item.id }));

    const itemsToUnmap =
      mappedVendorItemData?.items
        .filter((item) => items.filter((a) => a.id === item.id).length === 0)
        .map((item) => ({ vendorId: item.vendorId, vendorItemId: item.id, rawItemId: null })) ?? [];

    const results = await bulkUpdateVendorItemMapping([...itemsToMap, ...itemsToUnmap]);
    return results;
  }
  return useMutation<unknown, unknown, BulkUpdateVendorItemVariables, unknown>(mutationFunc, {});
};

export const useBulkCreateVendorItemMappingMutation = () => {
  const { bulkUpdateVendorItemMapping } = useInventoryRequests();

  async function mutationFunc({ items, rawItemId, vendorItemData }: BulkCreateVendorItemVariables) {
    const itemsToMap = items
      .filter((item) => vendorItemData?.items.filter((a) => a.id === item.id))
      .map((item) => ({ rawItemId, vendorId: item.vendorId, vendorItemId: item.id }));

    const results = await bulkUpdateVendorItemMapping([...itemsToMap]);
    return results;
  }
  return useMutation<unknown, unknown, BulkCreateVendorItemVariables, unknown>(mutationFunc, {
    useErrorBoundary: false,
  });
};

export const useCreateRawItemMutation = () => {
  const { createRawItem } = useInventoryRequests();

  async function mutationFunc(rawItemDto: RawItemDto) {
    const results = await createRawItem(rawItemDto);
    return results;
  }
  return useMutation<RawItemData, HttpsError, RawItemDto, unknown>(mutationFunc, {});
};

export const useUpdateReportingRecipeUnitsMutation = () => {
  const { updateReportingUnit, updateRecipeUnit } = useInventoryRequests();

  async function mutationFunc({ rawItemId, reportingUnit, recipeUnit }: UpdateReportingRecipeUnits) {
    const result1 = updateReportingUnit(rawItemId, reportingUnit);
    const result2 = updateRecipeUnit(rawItemId, recipeUnit);
    const results = await Promise.all([result1, result2]);
    return results;
  }
  return useMutation<unknown, HttpsError, UpdateReportingRecipeUnits, unknown>(mutationFunc, {});
};

export const useCreateRawItemNoDeactivationMutation = () => {
  const { createRawItemNoDeactivation } = useInventoryRequests();

  async function mutationFunc({ name, categoryId, isActive }: NoDeactivationItemVariables) {
    const results = await createRawItemNoDeactivation(name, categoryId, isActive);
    return results;
  }
  return useMutation<RawItemData, HttpsError, NoDeactivationItemVariables, unknown>(mutationFunc, {
    useErrorBoundary: false,
  });
};

export const useCreateCustomUnitMutation = () => {
  const { createCustomUnit } = useInventoryRequests();

  async function mutationFunc(customUnitDto: CustomUnitDto) {
    const results = await createCustomUnit(customUnitDto);
    return results;
  }
  return useMutation<CustomUnit | undefined, unknown, CustomUnitDto, unknown>(mutationFunc, {});
};

export const useUpdateCustomUnitMutation = () => {
  const { updateCustomUnitById } = useInventoryRequests();

  async function mutationFunc({ customUnitId, customUnitDto }: UpdateCustomUnitVariables) {
    const results = await updateCustomUnitById(customUnitId, customUnitDto);
    return results;
  }
  return useMutation<CustomUnit | undefined, unknown, UpdateCustomUnitVariables, unknown>(mutationFunc, {});
};

export const useUpdateRawItemMutation = () => {
  const { updateRawItem } = useInventoryRequests();

  async function mutationFunc({ rawItemId, rawItemDto }: UpdateRawItemVariables) {
    const results = await updateRawItem(rawItemId, rawItemDto);
    return results;
  }
  return useMutation<unknown, HttpsError, UpdateRawItemVariables, unknown>(mutationFunc, {});
};

export const useUpdateRawItemActiveStatusMutation = () => {
  const { updateRawItemActiveStatus } = useInventoryRequests();

  async function mutationFunc({ rawItemId, active, futureDeactivationDate }: UpdateRawItemStatusVariables) {
    const results = await updateRawItemActiveStatus(rawItemId, active, futureDeactivationDate);
    return results;
  }
  return useMutation<unknown, HttpsError, UpdateRawItemStatusVariables, unknown>(mutationFunc, {});
};

export const useMapCustomUnitMutation = () => {
  const { mapCustomUnit } = useInventoryRequests();
  async function mutationFunc({ customUnitMappingDto, rawItemId }: MapCustomUnitVariables) {
    const results = await mapCustomUnit(customUnitMappingDto, rawItemId);
    return results;
  }
  return useMutation<RawItemData | undefined, IApiError, MapCustomUnitVariables, unknown>(mutationFunc, {});
};

export const useMapIndustryUnitMutation = () => {
  const { mapIndustryUnit } = useInventoryRequests();
  async function mutationFunc({ unitId: industryUnitId, rawItemId }: MapIndustryUnitVariables) {
    const results = await mapIndustryUnit(industryUnitId, rawItemId);
    return results;
  }
  return useMutation<RawItemData | undefined, IApiError, MapIndustryUnitVariables, unknown>(mutationFunc, {});
};

export const useRemoveUnitMapping = () => {
  const { removeInventoryUnitMapping } = useInventoryRequests();
  async function mutationFunc({ inventoryUnitId, rawItemId }: DeleteInventoryUnitVariables) {
    const results = await removeInventoryUnitMapping(inventoryUnitId, rawItemId);
    return results;
  }
  return useMutation<unknown, unknown, DeleteInventoryUnitVariables, unknown>(mutationFunc, {});
};

export const useDeleteRawItemMutation = () => {
  const { deleteRawItem } = useInventoryRequests();
  async function mutationFunc({ rawItemId }: DeleteRawItemVariables) {
    const results = await deleteRawItem(rawItemId);
    return results;
  }
  return useMutation<unknown, unknown, DeleteRawItemVariables, unknown>(mutationFunc, {});
};

export const useCreateInvoice = () => {
  const { createInvoice } = useInventoryRequests();
  async function mutationFunc(invoiceDto: InvoiceDto) {
    const results = await createInvoice(invoiceDto);
    return results;
  }
  return useMutation<InvoiceData, unknown, InvoiceDto, unknown>(mutationFunc, {});
};

export const useUpdateInvoice = () => {
  const { updateInvoice } = useInventoryRequests();
  async function mutationFunc({ invoiceId, invoiceDto }: UpdateInvoiceVariables) {
    const results = await updateInvoice(invoiceId, invoiceDto);
    return results;
  }
  return useMutation<UpdateResponse, unknown, UpdateInvoiceVariables, unknown>(mutationFunc, {});
};

export const useUpdateInvoiceStatus = () => {
  const { updateInvoiceStatus } = useInventoryRequests();
  async function mutationFunc({ status, id, newItems }: UpdateInvoiceStatusVariables) {
    const results = await updateInvoiceStatus(status, id, newItems);
    return results;
  }
  return useMutation<unknown, unknown, UpdateInvoiceStatusVariables, unknown>(mutationFunc, {});
};

export const useDeleteInvoice = () => {
  const { deleteInvoice } = useInventoryRequests();
  async function mutationFunc({ invoiceId, isFinalized }: DeleteInvoiceVariables) {
    const results = await deleteInvoice(invoiceId, isFinalized);
    return results;
  }
  return useMutation<unknown, unknown, DeleteInvoiceVariables, unknown>(mutationFunc, {});
};

export const useEnableInvoiceEdit = () => {
  const { enableInvoiceEdit } = useInventoryRequests();
  async function mutationFunc(invoiceId: string) {
    const results = await enableInvoiceEdit(invoiceId);
    return results;
  }
  return useMutation<unknown, unknown, string, unknown>(mutationFunc, {});
};

export const useCreateInvoiceItem = (mutationOptions?: object) => {
  const { createInvoiceItemData } = useInventoryRequests();
  async function mutationFunc({ invoiceId, invoiceItemDto }: CreateInvoiceItemVariables) {
    const results = await createInvoiceItemData(invoiceId, invoiceItemDto);
    return results;
  }
  return useMutation<InvoiceItemData | null, unknown, CreateInvoiceItemVariables, unknown>(
    mutationFunc,
    mutationOptions ?? {}
  );
};

export const useUpdateInvoiceItem = (mutationOptions?: object) => {
  const { updateInvoiceItemData } = useInventoryRequests();
  async function mutationFunc({ invoiceId, invoiceItemId, invoiceItemDto }: UpdateInvoiceItemVariables) {
    const results = await updateInvoiceItemData(invoiceId, invoiceItemId, invoiceItemDto);
    return results;
  }
  return useMutation<UpdateBody, unknown, UpdateInvoiceItemVariables, unknown>(mutationFunc, mutationOptions ?? {});
};

export const useDeleteInvoiceItem = (mutationOptions?: object) => {
  const { deleteInvoiceItem } = useInventoryRequests();
  async function mutationFunc({ invoiceId, invoiceItemId }: DeleteInvoiceItemVariables) {
    const results = await deleteInvoiceItem(invoiceId, invoiceItemId);
    return results;
  }
  return useMutation<unknown, unknown, DeleteInvoiceItemVariables, unknown>(mutationFunc, mutationOptions ?? {});
};

export const useCreateNonDraftVendorItem = () => {
  const { createNonDraftVendorItemData } = useInventoryRequests();
  async function mutationFunc({ vendorId, vendorItemDto }: CreateVendorItemVariables) {
    const results = await createNonDraftVendorItemData(vendorId, vendorItemDto);
    return results;
  }
  return useMutation<VendorItemData | null, IApiError, CreateVendorItemVariables, unknown>(mutationFunc, {});
};

export const useCreateDraftVendorItem = () => {
  const { createDraftVendorItemData } = useInventoryRequests();
  async function mutationFunc({ vendorId, vendorItemDto }: CreateVendorItemVariables) {
    const results = await createDraftVendorItemData(vendorId, vendorItemDto);
    return results;
  }
  return useMutation<VendorItemData | null, unknown, CreateVendorItemVariables, unknown>(mutationFunc, {});
};

export const useUpdateNonDraftVendorItem = () => {
  const { updateNonDraftVendorItemData } = useInventoryRequests();
  async function mutationFunc({ vendorId, vendorItemId, vendorItemDto }: UpdateVendorItemVariables) {
    const results = await updateNonDraftVendorItemData(vendorId, vendorItemId, vendorItemDto);
    return results;
  }
  return useMutation<UpdateResponse, IApiError, UpdateVendorItemVariables, unknown>(mutationFunc, {});
};

export const useUpdateVendorItemActiveStatus = () => {
  const { updateVendorItemActiveStatus } = useInventoryRequests();
  async function mutationFunc({ vendorId, vendorItemId, isActive }: UpdateVendorItemStatusVariables) {
    const results = await updateVendorItemActiveStatus(vendorId, vendorItemId, isActive);
    return results;
  }
  return useMutation<unknown, unknown, UpdateVendorItemStatusVariables, unknown>(mutationFunc, {});
};

export const useCreateNonDraftVendor = () => {
  const { createNonDraftVendor } = useInventoryRequests();
  async function mutationFunc(vendorDto: VendorDto) {
    const results = await createNonDraftVendor(vendorDto);
    return results;
  }
  return useMutation<VendorData, unknown, VendorDto, unknown>(mutationFunc, {});
};

export const useCreateDraftVendor = () => {
  const { createDraftVendor } = useInventoryRequests();
  async function mutationFunc(vendorDto: VendorDto) {
    const results = await createDraftVendor(vendorDto);
    return results;
  }
  return useMutation<VendorData, unknown, VendorDto, unknown>(mutationFunc, {});
};

export const useUpdateVendor = () => {
  const { updateVendor } = useInventoryRequests();
  async function mutationFunc({ vendorId, vendorDto }: UpdateVendorVariables) {
    const results = await updateVendor(vendorId, vendorDto);
    return results;
  }
  return useMutation<UpdateResponse, unknown, UpdateVendorVariables, unknown>(mutationFunc, {});
};

export const useSetVendorActive = () => {
  const { setVendorActive } = useInventoryRequests();
  async function mutationFunc({ vendorId, isActive }: SetVendorActiveVariables) {
    const results = await setVendorActive(vendorId, isActive);
    return results;
  }
  return useMutation<unknown, unknown, SetVendorActiveVariables, unknown>(mutationFunc, {});
};

export const useAddOcrMetadata = () => {
  const { addOcrMetadata } = useInventoryRequests();
  async function mutationFunc({ invoiceId, enterpriseUnit, ocrMetadata }: AddOcrMetadataVariables) {
    const results = await addOcrMetadata(invoiceId, enterpriseUnit, ocrMetadata);
    return results;
  }
  return useMutation<unknown, unknown, AddOcrMetadataVariables, unknown>(mutationFunc, {});
};

export const useUpdateOcrMetadata = () => {
  const { updateOcrMetadata } = useInventoryRequests();
  async function mutationFunc({ invoiceId, enterpriseUnit, ocrMetadata }: UpdateOcrMetadataVariables) {
    const results = await updateOcrMetadata(invoiceId, enterpriseUnit, ocrMetadata);
    return results;
  }
  return useMutation<unknown, unknown, UpdateOcrMetadataVariables, unknown>(mutationFunc, {});
};

export const useUpdateLocationItemUnit = () => {
  const { updateLocationItemUnit } = useInventoryRequests();
  async function mutationFunc({ locationId, body }: UpdateLocationItemVariables) {
    const results = await updateLocationItemUnit(locationId, body);
    return results;
  }
  return useMutation<unknown, unknown, UpdateLocationItemVariables, unknown>(mutationFunc, {});
};

export const useUpdateInventoryList = () => {
  const { updateInventoryList } = useInventoryRequests();
  async function mutationFunc({ listId, body }: UpdateInventoryListVariables) {
    const results = await updateInventoryList(listId, body);
    return results;
  }
  return useMutation<unknown, unknown, UpdateInventoryListVariables, unknown>(mutationFunc, {});
};

export const useDeleteLocationItem = () => {
  const { deleteLocationItem } = useInventoryRequests();
  async function mutationFunc({ locationId, locationItemId }: DeleteLocationItemVariables) {
    const results = await deleteLocationItem(locationId, locationItemId);
    return results;
  }
  return useMutation<unknown, unknown, DeleteLocationItemVariables, unknown>(mutationFunc, {});
};

export const useBulkUpdateLocations = () => {
  const { bulkUpdateLocations } = useInventoryRequests();
  async function mutationFunc({ body }: BulkUpdateLocationsSchema) {
    const results = await bulkUpdateLocations(body);
    return results;
  }
  return useMutation<UpdateResponse, unknown, BulkUpdateLocationsSchema, unknown>(mutationFunc, {});
};

export const useCreateSalesItemMapping = () => {
  const { createSalesItemMapping } = useInventoryRequests();
  async function mutationFunc(body: SalesItemMappingData) {
    const results = await createSalesItemMapping(body);
    return results;
  }
  return useMutation<MappingId, unknown, SalesItemMappingData, unknown>(mutationFunc, {});
};

export const useUpdateSalesItemMapping = () => {
  const { updateSalesItemMapping } = useInventoryRequests();
  async function mutationFunc({ id, body }: UpdateSalesItemMappingVariables) {
    const results = await updateSalesItemMapping(id, body);
    return results;
  }
  return useMutation<MappingId, unknown, UpdateSalesItemMappingVariables, unknown>(mutationFunc, {});
};

export const useDeleteSalesItemMapping = () => {
  const { deleteSalesItemMapping } = useInventoryRequests();
  async function mutationFunc(id: string) {
    const results = await deleteSalesItemMapping(id);
    return results;
  }
  return useMutation<MappingId, unknown, string, unknown>(mutationFunc, {});
};

export const useGetRecipeItemUnitPrice = () => {
  const { getRecipeItemUnitPrice } = useInventoryRequests();
  async function mutationFunc(recipeItemUnitPriceResponse: RecipeItemUnitPriceRequest) {
    const results = await getRecipeItemUnitPrice(recipeItemUnitPriceResponse);
    return results;
  }
  return useMutation<RecipeItemUnitPriceResponse, IApiError, RecipeItemUnitPriceRequest, unknown>(mutationFunc, {});
};

export const useUpdateLocationSummary = () => {
  const { updateInventorySummaryLocation } = useInventoryRequests();
  async function mutationFunc({ summaryId, locationId, body, includePrepItems }: UpdateLocationSummaryVariables) {
    const results = await updateInventorySummaryLocation(summaryId, locationId, body, { includePrepItems });
    return results;
  }
  return useMutation<unknown, unknown, UpdateLocationSummaryVariables, unknown>(mutationFunc, {});
};

export const useDeleteListSummary = () => {
  const { deleteInventorySummaryList } = useInventoryRequests();
  async function mutationFunc({ id }: { id: string }) {
    const results = await deleteInventorySummaryList(id);
    return results;
  }
  return useMutation<unknown, unknown, { id: string }, unknown>(mutationFunc, {});
};

export const useUpdateCountSummary = () => {
  const { updateCountSummary } = useInventoryRequests();
  async function mutationFunc({ summaryId, body, includePrepItems }: UpdateSummaryVariables) {
    const results = await updateCountSummary(summaryId, body, includePrepItems);
    return results;
  }
  return useMutation<unknown, unknown, UpdateSummaryVariables, unknown>(mutationFunc, {});
};

export const useCreateInventorySummary = () => {
  const { createInventorySummary } = useInventoryRequests();

  async function mutationFunc({
    body,
    includePrepItems,
  }: {
    body: CreateInventorySummaryDto;
    includePrepItems?: boolean;
  }) {
    const results = await createInventorySummary(body, includePrepItems);
    return results;
  }
  return useMutation<
    InventorySummaryList,
    unknown,
    { body: CreateInventorySummaryDto; includePrepItems?: boolean },
    unknown
  >(mutationFunc, {});
};

export const useUpdateUsagePer = () => {
  const { updateUsagePer } = useInventoryRequests();
  async function mutationFunc({ rawItemId, body }: UpdateUsagePerDto) {
    const result = await updateUsagePer(rawItemId, body);
    return result;
  }
  return useMutation<number, unknown, UpdateUsagePerDto, unknown>(mutationFunc, {
    useErrorBoundary: false,
  });
};

export const useUpsertVendorOrderDeliveryPairs = () => {
  const { upsertVendorOrderDeliveryPairs } = useInventoryRequests();

  async function mutationFunc({ id, body }: UpsertOrderDeliveryPairsVariables) {
    const results = await upsertVendorOrderDeliveryPairs(id, body);
    return results;
  }
  return useMutation<string, unknown, UpsertOrderDeliveryPairsVariables, unknown>(mutationFunc, {});
};

export const useUpdatePurchaseOrderAcknowledgementStatus = () => {
  const { updatePurchaseOrderAcknowledgementStatus } = useInventoryRequests();

  async function mutationFunc({ id, nepOrganization }: { id: string; nepOrganization: string }) {
    const results = await updatePurchaseOrderAcknowledgementStatus(id, nepOrganization);
    return results;
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return useMutation<AxiosResponse<any, any>, unknown, any, unknown>(mutationFunc, {});
};

export const useCalculateUsagePerMutation = () => {
  const { calculateSiteUsagePer } = useInventoryRequests();

  return useMutation(
    (variables: { startDate: string; endDate: string }) =>
      calculateSiteUsagePer({ startDate: variables.startDate, endDate: variables.endDate }),
    { useErrorBoundary: false }
  );
};

export const useSendPOEmailToVendor = () => {
  const { sendPurchaseOrderEmail } = useInventoryRequests();

  async function mutationFunc({ id, userEmail }: { id: string; userEmail: string }) {
    const results = await sendPurchaseOrderEmail(id, userEmail);
    return results;
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return useMutation<unknown, unknown, any, unknown>(mutationFunc, {});
};

export const useSendPOFtpToVendor = () => {
  const { sendPurchaseOrderFtp } = useInventoryRequests();

  async function mutationFunc({ id }: { id: string }) {
    const results = await sendPurchaseOrderFtp(id);
    return results;
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return useMutation<unknown, unknown, any, unknown>(mutationFunc, {});
};

export const useCompletePurchaseOrder = () => {
  const { completePurchaseOrder } = useInventoryRequests();

  async function mutationFunc(id: string) {
    const results = await completePurchaseOrder(id);
    return results;
  }
  return useMutation<unknown, unknown, string, unknown>(mutationFunc, {});
};

export const useUpdateVendorSftpPassword = () => {
  const { updateVendorSftpPassword } = useInventoryRequests();

  async function mutationFunc({ vendorId, password }: { vendorId: string; password: string }) {
    const results = await updateVendorSftpPassword(vendorId, password);
    return results;
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return useMutation<unknown, unknown, any, unknown>(mutationFunc, {});
};

export const useGeneratePoNumber = () => {
  const { generatePoNumber } = useInventoryRequests();

  async function mutationFunc(vendorId: string) {
    const results = await generatePoNumber(vendorId);
    return results;
  }
  return useMutation<GeneratePoNumberResponseDto, unknown, string, unknown>(mutationFunc, {});
};

export const useCreatePurchaseOrder = () => {
  const { createPurchaseOrder } = useInventoryRequests();

  async function mutationFunc(body: CreatePurchaseOrderDto) {
    const results = await createPurchaseOrder(body);
    return results;
  }
  return useMutation<PurchaseOrderDetails, unknown, CreatePurchaseOrderDto, unknown>(mutationFunc, {});
};

export const useDeletePurchaseOrder = () => {
  const { deletePurchaseOrder } = useInventoryRequests();

  async function mutationFunc(id: string) {
    const results = await deletePurchaseOrder(id);
    return results;
  }
  return useMutation<unknown, unknown, string, unknown>(mutationFunc, {});
};

export const useUpdatePurchaseOrder = () => {
  const { updatePurchaseOrder } = useInventoryRequests();

  async function mutationFunc({ id, body }: { id: string; body: CreatePurchaseOrderDto }) {
    const results = await updatePurchaseOrder(id, body);
    return results;
  }
  return useMutation<PurchaseOrderDetails, unknown, { id: string; body: CreatePurchaseOrderDto }, unknown>(
    mutationFunc,
    {}
  );
};

export const useCreatePurchaseOrderItem = () => {
  const { createPurchaseOrderItem } = useInventoryRequests();

  async function mutationFunc({ id, body }: { id: string; body: CreatePurchaseOrderItemDto }) {
    const results = await createPurchaseOrderItem(id, body);
    return results;
  }
  return useMutation<PurchaseOrderItem, unknown, { id: string; body: CreatePurchaseOrderItemDto }, unknown>(
    mutationFunc,
    {}
  );
};

export const useUpdatePurchaseOrderItem = () => {
  const { updatePurchaseOrderItem } = useInventoryRequests();

  async function mutationFunc({
    purchaseOrderId,
    itemId,
    body,
  }: {
    purchaseOrderId: string;
    itemId: string;
    body: UpdatePurchaseOrderItemDto;
  }) {
    const results = await updatePurchaseOrderItem(purchaseOrderId, itemId, body);
    return results;
  }
  return useMutation<
    PurchaseOrderItem,
    unknown,
    { purchaseOrderId: string; itemId: string; body: UpdatePurchaseOrderItemDto },
    unknown
  >(mutationFunc, {});
};

export const useDeletePurchaseOrderItem = () => {
  const { deletePurchaseOrderItem } = useInventoryRequests();

  async function mutationFunc({ purchaseOrderId, itemId }: { purchaseOrderId: string; itemId: string }) {
    const results = await deletePurchaseOrderItem(purchaseOrderId, itemId);
    return results;
  }
  return useMutation<unknown, unknown, { purchaseOrderId: string; itemId: string }, unknown>(mutationFunc, {});
};

export const useBulkDeletePurchaseOrderItems = () => {
  const { bulkDeletePurchaseOrderItems } = useInventoryRequests();

  async function mutationFunc({ id, itemIds }: { id: string; itemIds: string[] }) {
    const results = await bulkDeletePurchaseOrderItems(id, itemIds);
    return results;
  }
  return useMutation<unknown, unknown, { id: string; itemIds: string[] }, unknown>(mutationFunc, {});
};

export const useCreateSuggestedPurchaseOrder = () => {
  const { createSuggestedPurchaseOrder } = useInventoryRequests();

  async function mutationFunc(payload: CreateSuggestedPurchaseOrderDto) {
    const results = await createSuggestedPurchaseOrder(payload);
    return results;
  }
  return useMutation<CreateSuggestedPurchaseOrderResponseDto, unknown, CreateSuggestedPurchaseOrderDto, unknown>(
    mutationFunc,
    {}
  );
};

export const useCreateInvoiceTemplate = () => {
  const { createInvoiceTemplate } = useInventoryRequests();

  async function mutationFunc({ vendorId, body }: CreateInvoiceTemplateVariables) {
    const results = await createInvoiceTemplate(vendorId, body);
    return results;
  }
  return useMutation<InvoiceTemplate, HttpsError, CreateInvoiceTemplateVariables, unknown>(mutationFunc, {});
};
